/**
 * Filter Item Row Component
 *
 * Individual row for a calendar or family label with color dot,
 * name, optional subtitle, and checkbox toggle.
 * Handles hidden calendars (Android) with greyed out state and info tooltip.
 */

import { useRef, useState } from 'react';
import { Pressable, StyleSheet, Switch, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { InfoPopover } from '@/components/ui/InfoPopover';
import type { FilterItem } from '@/types/calendar-filter';

type FilterItemRowProps = {
  item: FilterItem;
  onToggle: (item: FilterItem) => void;
};

export function FilterItemRow({ item, onToggle }: FilterItemRowProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const infoIconRef = useRef<View>(null);
  const [showPopover, setShowPopover] = useState(false);

  const isHidden = item.isHiddenInNativeApp === true;

  return (
    <View style={[
      styles.container,
      { borderBottomColor: palette.border },
      isHidden && styles.hiddenRow,
    ]}>
      <View style={styles.leftContent}>
        <View style={[
          styles.colorDot,
          { backgroundColor: item.color },
          isHidden && styles.hiddenDot,
        ]} />
        <View style={styles.textContainer}>
          <Text style={[
            styles.name,
            { color: palette.text },
            isHidden && { color: palette.textMuted },
          ]}>
            {item.name}
            {item.isPrimary && (
              <Text style={[styles.badge, { color: palette.textMuted }]}> (Primary)</Text>
            )}
          </Text>
          {item.subtitle && (
            <Text style={[styles.subtitle, { color: palette.textMuted }]}>
              {item.subtitle}
            </Text>
          )}
        </View>
        {isHidden && (
          <>
            <Pressable
              ref={infoIconRef}
              onPress={() => setShowPopover(true)}
              hitSlop={8}
              style={styles.infoIconButton}
            >
              <Ionicons
                name="information-circle-outline"
                size={20}
                color={palette.textMuted}
              />
            </Pressable>
            <InfoPopover
              visible={showPopover}
              onClose={() => setShowPopover(false)}
              message="To see this calendar, toggle 'show' in your Google Calendar app"
              from={infoIconRef}
            />
          </>
        )}
      </View>

      <Switch
        value={item.isSelected}
        onValueChange={() => onToggle(item)}
        trackColor={{ false: palette.border, true: palette.primary }}
        thumbColor={item.isSelected ? '#FFFFFF' : '#F4F3F4'}
        ios_backgroundColor={palette.border}
        disabled={isHidden}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderBottomWidth: 1,
  },
  hiddenRow: {
    opacity: 0.6,
  },
  leftContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: Spacing.md,
    paddingRight: Spacing.lg,
  },
  colorDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
  },
  hiddenDot: {
    opacity: 0.5,
  },
  textContainer: {
    flex: 1,
  },
  name: {
    fontSize: 14,
    fontWeight: '500',
    fontFamily: getBodyFont(),
  },
  badge: {
    fontSize: 12,
    fontWeight: '400',
    fontFamily: getBodyFont(),
  },
  subtitle: {
    fontSize: 12,
    marginTop: Spacing.xxs,
    fontFamily: getBodyFont(),
  },
  infoIconButton: {
    padding: Spacing.xxs,
  },
});
